/**
 * Servicio de Parseo de Documentos (PDF417 y MRZ TD1)
 * Cumple con .idx-rules.md: funciones menores a 40 líneas y lógica desacoplada.
 */

/**
 * Normaliza y formatea la fecha de nacimiento a YYYY-MM-DD.
 */
function normalizeDate(rawDate) {
  if (!rawDate) return null;
  const clean = rawDate.replace(/[\/\s-]/g, '');
  
  // Formato YYYYMMDD
  if (clean.length === 8 && /^\d{8}$/.test(clean)) {
    return `${clean.substring(0, 4)}-${clean.substring(4, 6)}-${clean.substring(6, 8)}`;
  }
  // Formato DDMMYYYY
  if (clean.length === 8 && /^\d{8}$/.test(clean) && parseInt(clean.substring(4, 8), 10) > 1900) {
    return `${clean.substring(4, 8)}-${clean.substring(2, 4)}-${clean.substring(0, 2)}`;
  }
  return null;
}

/**
 * Normaliza el género a 'M' o 'F'.
 */
function normalizeGender(rawSex) {
  const sex = (rawSex || '').toUpperCase().trim();
  if (sex === '1' || sex === 'M' || sex === 'MASCULINO') return 'M';
  if (sex === '2' || sex === 'F' || sex === 'FEMENINO') return 'F';
  return 'U'; // Desconocido
}

/**
 * Parseador para formato PDF417 fijo de RENIEC (Posicional de Bytes).
 */
function parseFixedPDF417(rawData) {
  const dni = rawData.substring(2, 10).trim();
  const apPaterno = rawData.substring(10, 50).trim();
  const apMaterno = rawData.substring(50, 90).trim();
  const nombres = rawData.substring(90, 125).trim();
  const sexo = normalizeGender(rawData.substring(125, 126));
  
  // La fecha de nacimiento suele estar al final de la cadena de texto base (byte 448)
  let fechaNac = null;
  if (rawData.length >= 456) {
    const rawDate = rawData.substring(448, 456).trim();
    fechaNac = normalizeDate(rawDate);
  }
  
  return {
    dni,
    fullName: `${nombres} ${apPaterno} ${apMaterno}`.trim(),
    gender: sexo,
    birthDate: fechaNac
  };
}

/**
 * Parseador para formato PDF417 delimitado (por caracteres como '.', '|', etc.).
 */
function parseDelimitedPDF417(rawData, delimiter) {
  const parts = rawData.split(delimiter).map(p => p.trim());
  if (parts.length < 5) return null;

  // Intentar mapear campos comunes
  const dni = parts[0].length === 8 && /^\d{8}$/.test(parts[0]) ? parts[0] : parts.find(p => /^\d{8}$/.test(p)) || '';
  const sexPart = parts.find(p => p === '1' || p === '2' || p === 'M' || p === 'F') || '';
  
  // Buscar fechas YYYYMMDD o DD/MM/YYYY
  const datePart = parts.find(p => /^\d{8}$/.test(p) && p !== dni) || 
                   parts.find(p => /^\d{2}[/-]\d{2}[/-]\d{4}$/.test(p)) || '';

  return {
    dni,
    fullName: parts.filter(p => p.length > 2 && !/^\d+$/.test(p) && p !== 'M' && p !== 'F').slice(0, 3).join(' '),
    gender: normalizeGender(sexPart),
    birthDate: normalizeDate(datePart) || datePart
  };
}

/**
 * Parsea un subarray de bytes crudos a string de caracteres legibles (ISO-8859-1).
 */
function decodeRawBytes(rawBytes, start, end) {
  let str = '';
  for (let i = start; i <= end; i++) {
    if (rawBytes[i] !== undefined) {
      str += String.fromCharCode(rawBytes[i]);
    }
  }
  return str.trim();
}

/**
 * Parseador para formato PDF417 de bytes crudos (RENIEC Posicional).
 * Evita el desfase cromático y de caracteres binarios en el bloque biométrico central.
 */
function parseFixedPDF417Bytes(rawBytes) {
  const dni = decodeRawBytes(rawBytes, 2, 9);
  const apPaterno = decodeRawBytes(rawBytes, 10, 49);
  const apMaterno = decodeRawBytes(rawBytes, 50, 89);
  const nombres = decodeRawBytes(rawBytes, 90, 124);
  const sexo = normalizeGender(decodeRawBytes(rawBytes, 125, 125));
  
  // La fecha de nacimiento física está exactamente en los bytes 448-455
  const rawDate = decodeRawBytes(rawBytes, 448, 455);
  const birthDate = normalizeDate(rawDate);
  
  return {
    dni,
    fullName: `${nombres} ${apPaterno} ${apMaterno}`.trim(),
    gender: sexo,
    birthDate: birthDate
  };
}

/**
 * Función principal para analizar y parsear código PDF417.
 * Soporta tanto string crudo como bytes crudos (prioridad).
 */
export function parsePDF417(rawData, rawBytes) {
  if (rawBytes && rawBytes.length >= 456) {
    const res = parseFixedPDF417Bytes(rawBytes);
    if (res && res.dni && /^\d{8}$/.test(res.dni)) return res;
  }

  if (!rawData) return null;
  
  // Limpiar saltos de línea para evitar desalineación de offsets fijos al copiar/pegar
  const cleanData = rawData.replace(/[\r\n]+/g, '');
  
  // Detectar delimitadores comunes
  const delimiters = ['|', ';', ','];
  const activeDelimiter = delimiters.find(d => cleanData.includes(d));

  if (activeDelimiter) {
    const res = parseDelimitedPDF417(cleanData, activeDelimiter);
    if (res && res.dni) return res;
  }

  // Si tiene un punto como separador
  if (cleanData.includes('.') && cleanData.split('.').length > 4) {
    const res = parseDelimitedPDF417(cleanData, '.');
    if (res && res.dni) return res;
  }

  // De lo contrario, usar posicional fijo si cumple con la longitud mínima de RENIEC
  if (cleanData.length >= 125) {
    const res = parseFixedPDF417(cleanData);
    if (res && res.dni && /^\d{8}$/.test(res.dni)) return res;
  }

  // Fallback: Buscar cualquier secuencia de 8 dígitos (formato DNI peruano)
  const match = cleanData.match(/\d{8}/);
  if (match) {
    return {
      dni: match[0],
      fullName: 'DNI Escaneado',
      gender: 'U',
      birthDate: null
    };
  }

  return null;
}

/**
 * Normaliza caracteres '<' que el OCR lee como 'K' o 'L' por vecindad.
 * Solo limpia 'K' y 'L' si forman bloques de ruido aislados de nombres reales.
 */
function cleanMRZLine(line) {
  return line.replace(/[^<]+/g, (match) => {
    if (/^[KLkl]+$/.test(match)) {
      return '<'.repeat(match.length);
    }
    return match;
  });
}

/**
 * Parsea el nombre y apellidos de la tercera línea del bloque MRZ.
 */
function parseMRZName(cleanedL3) {
  const match = cleanedL3.match(/<{2,}/);
  if (!match) {
    return cleanedL3.replace(/</g, ' ').replace(/\s+/g, ' ').trim();
  }
  const index = match.index;
  const surnames = cleanedL3.substring(0, index).replace(/</g, ' ').replace(/\s+/g, ' ').trim();
  const givenNames = cleanedL3.substring(index + match[0].length).replace(/</g, ' ').replace(/\s+/g, ' ').trim();
  return `${givenNames} ${surnames}`.trim();
}

/**
 * Extrae la fecha de nacimiento y la formatea a YYYY-MM-DD.
 */
function l2Birth(rawBirth) {
  if (!rawBirth || rawBirth.length < 6) return null;
  const currentYear = new Date().getFullYear() % 100;
  const yy = parseInt(rawBirth.substring(0, 2), 10);
  const mm = rawBirth.substring(2, 4);
  const dd = rawBirth.substring(4, 6);
  const century = yy <= currentYear ? '20' : '19';
  return `${century}${yy}-${mm}-${dd}`;
}

/**
 * Parseador para zona MRZ (ICAO Doc 9303, estándar TD1: 3 líneas de 30 caracteres).
 * Razón: Procesa el reverso de DNIs electrónicos y soporta ruidos de OCR.
 */
export function parseMRZ(mrzText) {
  if (!mrzText) return null;
  const rawLines = mrzText.split('\n')
    .map(line => line.replace(/[^A-Z0-9<]/gi, '').toUpperCase().trim())
    .filter(line => line.length > 10);

  let l1 = null, l2 = null, l3 = null;
  for (let i = 0; i < rawLines.length; i++) {
    if (rawLines[i].includes('PER') && i < rawLines.length - 2) {
      l1 = rawLines[i];
      l2 = rawLines[i + 1];
      l3 = rawLines[i + 2];
      break;
    }
  }
  if (!l1 || !l2 || !l3) return null;

  const cleanL1 = cleanMRZLine(l1);
  const cleanL2 = cleanMRZLine(l2);
  const cleanL3 = cleanMRZLine(l3);

  const perIdx = cleanL1.indexOf('PER');
  if (perIdx === -1) return null;
  const dni = cleanL1.substring(perIdx + 3, perIdx + 11);

  const l2Match = cleanL2.match(/\d{6}\d[A-Z0-9<]/);
  if (!l2Match) return null;
  const birthDate = l2Birth(l2Match[0].substring(0, 6));
  const gender = normalizeGender(l2Match[0].substring(7, 8));
  const fullName = parseMRZName(cleanL3);

  return { dni, fullName, gender, birthDate };
}
